import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

const connectivityColor = (status) => {
  if (status === 'Online') return 'success';
  if (status === 'Offline') return 'error';
  return 'default';
};

const stabilityColor = (label) => {
  if (label === 'Stable') return 'success';
  if (label === 'Irregular' || label === 'Disconnected') return 'error';
  if (label === 'Sparse' || label === 'No Data') return 'warning';
  return 'default';
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const DeviceDebugReport = ({ schools = [], agencies = [] }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({
    device_id: '',
    parent_name: '',
    school_id: '',
    agency_id: '',
    connectivity: 'all'
  });

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.set(key, value);
        }
      });

      const response = await fetch(`${API_BASE_URL}/admin/device-debug-report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to load device debug report');
      }

      const data = await response.json();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setItems([]);
      setError(err.message || 'Failed to load device debug report');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const openDetail = async (deviceId) => {
    try {
      setDetailOpen(true);
      setDetailLoading(true);
      setDetail(null);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/admin/device-debug-report/${encodeURIComponent(deviceId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to load device details');
      }
      setDetail(await response.json());
    } catch (err) {
      setError(err.message || 'Failed to load device details');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const summary = useMemo(() => {
    const online = items.filter((item) => item.connectivityStatus === 'Online').length;
    const irregular = items.filter((item) => ['Irregular', 'Disconnected', 'Sparse', 'No Data'].includes(item.stabilityLabel)).length;
    return { total: items.length, online, irregular };
  }, [items]);

  return (
    <Box>
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Device Debug Report</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Full device chain: Agency → School → Teacher → Parent → Student, plus connectivity and stability for debugging.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Device ID / IMEI"
              value={filters.device_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, device_id: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Parent name / phone"
              value={filters.parent_name}
              onChange={(e) => setFilters((prev) => ({ ...prev, parent_name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>School</InputLabel>
              <Select
                label="School"
                value={filters.school_id}
                onChange={(e) => setFilters((prev) => ({ ...prev, school_id: e.target.value }))}
              >
                <MenuItem value="">All schools</MenuItem>
                {schools.map((school) => (
                  <MenuItem key={school.id} value={school.id}>{school.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Agency</InputLabel>
              <Select
                label="Agency"
                value={filters.agency_id}
                onChange={(e) => setFilters((prev) => ({ ...prev, agency_id: e.target.value }))}
              >
                <MenuItem value="">All agencies</MenuItem>
                {agencies.map((agency) => (
                  <MenuItem key={agency.id} value={agency.id}>{agency.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Connectivity</InputLabel>
              <Select
                label="Connectivity"
                value={filters.connectivity}
                onChange={(e) => setFilters((prev) => ({ ...prev, connectivity: e.target.value }))}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center" flexWrap="wrap">
          <Button variant="contained" onClick={loadReport} disabled={loading}>Apply Filters</Button>
          <Chip label={`Total: ${summary.total}`} />
          <Chip label={`Online: ${summary.online}`} color="success" variant="outlined" />
          <Chip label={`Needs attention: ${summary.irregular}`} color="warning" variant="outlined" />
        </Stack>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Device ID</TableCell>
                  <TableCell>Agency</TableCell>
                  <TableCell>School</TableCell>
                  <TableCell>Teacher</TableCell>
                  <TableCell>Parent</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Connectivity</TableCell>
                  <TableCell>Stability</TableCell>
                  <TableCell>Last seen</TableCell>
                  <TableCell align="right">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">No devices found for selected filters.</TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={`${item.internalDeviceId}-${item.studentId || 'none'}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{item.deviceId || '—'}</Typography>
                        {item.simNumber && (
                          <Typography variant="caption" color="text.secondary">SIM: {item.simNumber}</Typography>
                        )}
                      </TableCell>
                      <TableCell>{item.agencyName || '—'}</TableCell>
                      <TableCell>{item.schoolName || '—'}</TableCell>
                      <TableCell>{item.teacherName || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.parentName || '—'}</Typography>
                        {item.parentPhone && (
                          <Typography variant="caption" color="text.secondary">{item.parentPhone}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.studentName || '—'}</Typography>
                        {(item.standard || item.division) && (
                          <Typography variant="caption" color="text.secondary">
                            {[item.standard, item.division].filter(Boolean).join(' - ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.connectivityStatus}
                          color={connectivityColor(item.connectivityStatus)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.stabilityLabel}
                          color={stabilityColor(item.stabilityLabel)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDateTime(item.lastSeen)}</Typography>
                        {item.minutesSinceSeen != null && (
                          <Typography variant="caption" color="text.secondary">
                            {item.minutesSinceSeen} min ago
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ViewIcon />}
                          onClick={() => openDetail(item.deviceId)}
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Device Technical Details</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : detail ? (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>Assignment chain</Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary">Device</Typography><Typography>{detail.device.deviceId}</Typography></Grid>
                  <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary">Agency</Typography><Typography>{detail.device.agencyName || '—'}</Typography></Grid>
                  <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary">School</Typography><Typography>{detail.device.schoolName || '—'}</Typography></Grid>
                  <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary">Teacher</Typography><Typography>{detail.assignment.teacherName || '—'}</Typography></Grid>
                  <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary">Parent</Typography><Typography>{detail.assignment.parentName || '—'}</Typography></Grid>
                  <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary">Student</Typography><Typography>{detail.assignment.studentName || '—'}</Typography></Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight={600}>Contact numbers</Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Parent phone</Typography><Typography>{detail.assignment.parentPhone || '—'}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">SOS</Typography><Typography>{detail.contacts?.sosNumber || '—'}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Mom</Typography><Typography>{detail.contacts?.momNumber || '—'}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Dad</Typography><Typography>{detail.contacts?.dadNumber || '—'}</Typography></Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight={600}>Connectivity & stability</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }} flexWrap="wrap">
                  <Chip label={detail.technical.connectivityStatus} color={connectivityColor(detail.technical.connectivityStatus)} size="small" />
                  <Chip label={`Stability: ${detail.technical.stability.label}`} color={stabilityColor(detail.technical.stability.label)} size="small" variant="outlined" />
                  {detail.technical.connectionStatus && <Chip label={detail.technical.connectionStatus} size="small" />}
                  {detail.technical.movementState && <Chip label={detail.technical.movementState} size="small" variant="outlined" />}
                </Stack>
                <Grid container spacing={1}>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Last seen</Typography><Typography>{formatDateTime(detail.technical.lastSeen)}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Last location</Typography><Typography>{formatDateTime(detail.technical.lastLocationAt)}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Locations (48h)</Typography><Typography>{detail.technical.stability.locationCount}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Max gap (min)</Typography><Typography>{detail.technical.stability.maxGapMinutes ?? '—'}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Avg interval (min)</Typography><Typography>{detail.technical.stability.avgIntervalMinutes ?? '—'}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Gaps &gt; 30 min</Typography><Typography>{detail.technical.stability.gapsOverThreshold}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Power-off estimate</Typography><Typography>{detail.technical.stability.powerOffEstimate}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Low battery events</Typography><Typography>{detail.technical.stability.lowBatteryEvents}</Typography></Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight={600}>Battery</Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={4}><Typography variant="caption" color="text.secondary">Current</Typography><Typography>{detail.technical.battery.current != null ? `${detail.technical.battery.current}%` : '—'}</Typography></Grid>
                  <Grid item xs={4}><Typography variant="caption" color="text.secondary">Min (48h)</Typography><Typography>{detail.technical.battery.min24h != null ? `${detail.technical.battery.min24h}%` : '—'}</Typography></Grid>
                  <Grid item xs={4}><Typography variant="caption" color="text.secondary">Max (48h)</Typography><Typography>{detail.technical.battery.max24h != null ? `${detail.technical.battery.max24h}%` : '—'}</Typography></Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight={600}>Recent locations (last 20)</Typography>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Lat</TableCell>
                      <TableCell>Lng</TableCell>
                      <TableCell>Battery</TableCell>
                      <TableCell>Accuracy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.technical.recentLocations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>No recent location records.</TableCell>
                      </TableRow>
                    ) : (
                      detail.technical.recentLocations.map((row, index) => (
                        <TableRow key={`${row.eventTime}-${index}`}>
                          <TableCell>{formatDateTime(row.eventTime)}</TableCell>
                          <TableCell>{row.latitude ?? '—'}</TableCell>
                          <TableCell>{row.longitude ?? '—'}</TableCell>
                          <TableCell>{row.batteryLevel != null ? `${row.batteryLevel}%` : '—'}</TableCell>
                          <TableCell>{row.accuracy ?? '—'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceDebugReport;
